import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { site } from "../mock";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const empty = {
  name: "",
  email: "",
  phone: "",
  destination: "",
  period: "",
  persons: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Vul minimaal je naam en e-mailadres in.");
      return;
    }
    setSubmitting(true);
    // Frontend-only mock: store the request locally
    setTimeout(() => {
      const existing = JSON.parse(localStorage.getItem("reisaanvragen") || "[]");
      existing.push({ ...form, date: new Date().toISOString() });
      localStorage.setItem("reisaanvragen", JSON.stringify(existing));
      setSubmitting(false);
      setForm(empty);
      toast.success("Bedankt! Je aanvraag is verstuurd. Ik neem snel contact met je op.");
    }, 700);
  };

  return (
    <section id="contact" className="py-24 bg-[#fbfaf6]">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
        {/* Info */}
        <div className="reveal">
          <span className="text-xs font-600 uppercase tracking-[0.25em] text-[#86233a]">Contact</span>
          <h2 className="font-display mt-4 text-4xl lg:text-5xl font-600 text-[#1d1c1a] leading-tight">
            Klaar voor je volgende avontuur?
          </h2>
          <p className="mt-5 text-lg text-[#4b4642] max-w-md">
            Vertel me kort waar je van droomt en ik neem vrijblijvend contact met
            je op. Samen maken we er iets moois van.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${site.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ffd7d4] text-[#86233a] group-hover:bg-[#86233a] group-hover:text-white transition-colors">
                <Phone size={20} />
              </span>
              <div>
                <p className="text-sm text-[#4b4642]">Bel of app</p>
                <p className="font-500 text-[#1d1c1a]">{site.phone}</p>
              </div>
            </a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-4 group">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ffd7d4] text-[#86233a] group-hover:bg-[#86233a] group-hover:text-white transition-colors">
                <Mail size={20} />
              </span>
              <div>
                <p className="text-sm text-[#4b4642]">Mail me</p>
                <p className="font-500 text-[#1d1c1a] break-all">{site.email}</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ffd7d4] text-[#86233a]">
                <MapPin size={20} />
              </span>
              <div>
                <p className="text-sm text-[#4b4642]">Werkgebied</p>
                <p className="font-500 text-[#1d1c1a]">{site.region}</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex gap-3">
            <a href={site.socials.facebook} target="_blank" rel="noreferrer" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#530e2f]/20 text-[#530e2f] hover:bg-[#86233a] hover:text-white hover:border-[#86233a] transition-colors">
              <Facebook size={18} />
            </a>
            <a href={site.socials.instagram} target="_blank" rel="noreferrer" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#530e2f]/20 text-[#530e2f] hover:bg-[#86233a] hover:text-white hover:border-[#86233a] transition-colors">
              <Instagram size={18} />
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="reveal">
          <form onSubmit={handleSubmit} className="rounded-[1.75rem] bg-white border border-[#eadfce] p-8 shadow-[0_25px_60px_-30px_rgba(83,14,47,0.4)]">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-[#530e2f]">Naam *</Label>
                <Input id="name" value={form.name} onChange={update("name")} placeholder="Je naam" className="mt-2 bg-[#fbfaf6] border-[#e3d7c4] focus-visible:ring-[#86233a]" />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#530e2f]">E-mail *</Label>
                <Input id="email" type="email" value={form.email} onChange={update("email")} placeholder="je@email.nl" className="mt-2 bg-[#fbfaf6] border-[#e3d7c4] focus-visible:ring-[#86233a]" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#530e2f]">Telefoon</Label>
                <Input id="phone" value={form.phone} onChange={update("phone")} placeholder="06 - ..." className="mt-2 bg-[#fbfaf6] border-[#e3d7c4] focus-visible:ring-[#86233a]" />
              </div>
              <div>
                <Label htmlFor="destination" className="text-[#530e2f]">Bestemming</Label>
                <Input id="destination" value={form.destination} onChange={update("destination")} placeholder="Waar wil je heen?" className="mt-2 bg-[#fbfaf6] border-[#e3d7c4] focus-visible:ring-[#86233a]" />
              </div>
              <div>
                <Label htmlFor="period" className="text-[#530e2f]">Reisperiode</Label>
                <Input id="period" value={form.period} onChange={update("period")} placeholder="Bijv. juli 2025" className="mt-2 bg-[#fbfaf6] border-[#e3d7c4] focus-visible:ring-[#86233a]" />
              </div>
              <div>
                <Label htmlFor="persons" className="text-[#530e2f]">Aantal personen</Label>
                <Input id="persons" value={form.persons} onChange={update("persons")} placeholder="Bijv. 2 volw. + 2 kind." className="mt-2 bg-[#fbfaf6] border-[#e3d7c4] focus-visible:ring-[#86233a]" />
              </div>
            </div>
            <div className="mt-5">
              <Label htmlFor="message" className="text-[#530e2f]">Jouw wensen</Label>
              <Textarea id="message" value={form.message} onChange={update("message")} rows={4} placeholder="Vertel me over je droomreis..." className="mt-2 bg-[#fbfaf6] border-[#e3d7c4] focus-visible:ring-[#86233a]" />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#86233a] px-7 py-3.5 text-white font-500 hover:bg-[#530e2f] transition-colors duration-300 disabled:opacity-70 shadow-[0_10px_30px_-10px_rgba(134,35,58,0.6)]"
            >
              {submitting ? "Versturen..." : "Verstuur aanvraag"}
              {!submitting && <Send size={17} />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
